import React from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Chip,
  Alert,
  Collapse,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  CloudUpload as UploadIcon,
  Settings as ProcessingIcon,
  Refresh as RefreshIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Description as DocumentIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface UploadFile {
  id: string;
  file: File;
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'error' | 'duplicate';
  progress: number;
  error?: string;
  statusMessage?: string;
  isDuplicate?: boolean;
  duplicateDocumentId?: string;
  metadata?: any;
}

interface UploadProgressTrackerProps {
  uploadFiles: UploadFile[];
  onRetry: (fileId: string) => void;
}

const UploadProgressTracker: React.FC<UploadProgressTrackerProps> = ({
  uploadFiles,
  onRetry,
}) => {
  const { t } = useTranslation();
  const [expandedItems, setExpandedItems] = React.useState<Set<string>>(new Set());

  const getStatusIcon = (status: UploadFile['status']) => {
    switch (status) {
      case 'completed':
        return <CheckCircleIcon color="success" />;
      case 'error':
        return <ErrorIcon color="error" />;
      case 'duplicate':
        return <ErrorIcon color="warning" />;
      case 'uploading':
        return <UploadIcon color="primary" />;
      case 'processing':
        return <ProcessingIcon color="primary" />;
      default:
        return <DocumentIcon />;
    }
  };

  const getStatusColor = (status: UploadFile['status']) => {
    switch (status) {
      case 'completed':
        return 'success';
      case 'error':
        return 'error';
      case 'duplicate':
        return 'warning';
      case 'uploading':
      case 'processing':
        return 'primary';
      default:
        return 'default';
    }
  };

  const getStatusText = (status: UploadFile['status'], progress: number) => {
    switch (status) {
      case 'pending':
        return t('upload.status.pending');
      case 'uploading':
        return t('upload.status.uploading', { progress: Math.round(progress) });
      case 'processing':
        return t('upload.status.processing');
      case 'completed':
        return t('upload.status.completed');
      case 'error':
        return t('upload.status.error');
      case 'duplicate':
        return t('upload.status.duplicate');
      default:
        return t('upload.status.unknown');
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const toggleExpanded = (fileId: string) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(fileId)) {
      newExpanded.delete(fileId);
    } else {
      newExpanded.add(fileId);
    }
    setExpandedItems(newExpanded);
  };

  const completedCount = uploadFiles.filter(f => f.status === 'completed').length;
  const errorCount = uploadFiles.filter(f => f.status === 'error').length;
  const totalCount = uploadFiles.length;
  const overallProgress = totalCount > 0 ? (completedCount / totalCount) * 100 : 0;

  return (
    <Box>
      {/* Overall Progress */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('upload.progressTracker.overallProgress')}
          </Typography>
          <Box display="flex" alignItems="center" gap={2} sx={{ mb: 2 }}>
            <LinearProgress
              variant="determinate"
              value={overallProgress}
              sx={{ flexGrow: 1, height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2" color="text.secondary">
              {completedCount}/{totalCount}
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Chip
              label={t('upload.progressTracker.completedCount', { count: completedCount })}
              color="success"
              variant="outlined"
              size="small"
            />
            {errorCount > 0 && (
              <Chip
                label={t('upload.progressTracker.failedCount', { count: errorCount })}
                color="error"
                variant="outlined"
                size="small"
              />
            )}
          </Box>
        </CardContent>
      </Card>

      {/* Individual File Progress */}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            {t('upload.progressTracker.fileProgress')}
          </Typography>

          <List>
            {uploadFiles.map((uploadFile) => {
              const isExpanded = expandedItems.has(uploadFile.id);
              const hasError = uploadFile.status === 'error';
              const isActive = ['uploading', 'processing'].includes(uploadFile.status);

              return (
                <React.Fragment key={uploadFile.id}>
                  <ListItem
                    sx={{
                      border: '1px solid',
                      borderColor: hasError ? 'error.light' : 'divider',
                      borderRadius: 1,
                      mb: 1,
                      backgroundColor: hasError ? 'error.light' : 'transparent',
                      '&:hover': {
                        backgroundColor: hasError ? 'error.light' : 'action.hover',
                      },
                    }}
                  >
                    <ListItemIcon>
                      {getStatusIcon(uploadFile.status)}
                    </ListItemIcon>

                    <ListItemText
                      primaryTypographyProps={{ component: 'div' }}
                      secondaryTypographyProps={{ component: 'div' }}
                      primary={
                        <Box display="flex" alignItems="center" gap={1}>
                          <Typography variant="body1">
                            {uploadFile.file.name}
                          </Typography>
                          <Chip
                            label={getStatusText(uploadFile.status, uploadFile.progress)}
                            color={getStatusColor(uploadFile.status) as any}
                            size="small"
                            variant="outlined"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {formatFileSize(uploadFile.file.size)}
                          </Typography>
                          {isActive && (
                            <>
                              <LinearProgress
                                variant="determinate"
                                value={uploadFile.progress}
                                sx={{ mt: 1, height: 4, borderRadius: 2 }}
                              />
                              {uploadFile.statusMessage && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                                  {uploadFile.statusMessage}
                                </Typography>
                              )}
                            </>
                          )}
                        </Box>
                      }
                    />

                    <ListItemSecondaryAction>
                      <Box display="flex" alignItems="center" gap={1}>
                        {hasError && (
                          <IconButton
                            onClick={() => onRetry(uploadFile.id)}
                            color="primary"
                            size="small"
                          >
                            <RefreshIcon />
                          </IconButton>
                        )}
                        <IconButton
                          onClick={() => toggleExpanded(uploadFile.id)}
                          size="small"
                        >
                          {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                        </IconButton>
                      </Box>
                    </ListItemSecondaryAction>
                  </ListItem>

                  {/* Expanded Details */}
                  <Collapse in={isExpanded} timeout="auto">
                    <Card variant="outlined" sx={{ ml: 4, mr: 2, mb: 1 }}>
                      <CardContent sx={{ pb: '16px !important' }}>
                        <Typography variant="subtitle2" gutterBottom>
                          {t('upload.progressTracker.fileDetails')}
                        </Typography>

                        <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1, mb: 2 }}>
                          <Typography variant="caption" color="text.secondary">
                            {t('upload.progressTracker.fileSize')}
                          </Typography>
                          <Typography variant="caption">
                            {formatFileSize(uploadFile.file.size)}
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            {t('upload.progressTracker.fileType')}
                          </Typography>
                          <Typography variant="caption">
                            {uploadFile.file.type || 'Unknown'}
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            {t('upload.progressTracker.progress')}
                          </Typography>
                          <Typography variant="caption">
                            {Math.round(uploadFile.progress)}%
                          </Typography>
                        </Box>

                        {uploadFile.metadata && (
                          <Box>
                            <Typography variant="subtitle2" gutterBottom>
                              {t('upload.progressTracker.metadata')}
                            </Typography>
                            <Box sx={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                {t('upload.progressTracker.category')}
                              </Typography>
                              <Typography variant="caption">
                                {uploadFile.metadata.category}
                              </Typography>

                              <Typography variant="caption" color="text.secondary">
                                {t('upload.progressTracker.description')}
                              </Typography>
                              <Typography variant="caption">
                                {uploadFile.metadata.description}
                              </Typography>
                            </Box>
                          </Box>
                        )}

                        {uploadFile.error && (
                          <Alert severity="error" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                              {uploadFile.error}
                            </Typography>
                          </Alert>
                        )}

                        {uploadFile.status === 'processing' && uploadFile.statusMessage && (
                          <Alert severity="info" sx={{ mt: 2 }}>
                            <Typography variant="body2">
                              {uploadFile.statusMessage}
                            </Typography>
                          </Alert>
                        )}
                      </CardContent>
                    </Card>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </List>
        </CardContent>
      </Card>
    </Box>
  );
};

export default UploadProgressTracker;
